import {
  Dialog,
  DialogContent,
  DialogProps,
  makeStyles,
  Slide,
  SlideProps,
} from '@material-ui/core';
import { IconButton, Typography } from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import cn from 'classnames';
import { CSSProperties, forwardRef, MouseEvent, ReactNode } from 'react';
import { COLORS } from '../../theme/variables';
import { isMobile } from '../../utils/functions';
import Text from './text';

interface DialogTitleProps {
  id?: string;
  children: ReactNode;
  caption?: string;
  onClose: (event: MouseEvent) => any;
  className?: string;
}

interface ModalProps extends DialogProps {
  onClose: (event: MouseEvent) => any;
  width?: string;
  height?: string;
  style?: CSSProperties;
  classNameContent?: string;
  titleProps?: {
    title: ReactNode;
    titleIcon?: ReactNode;
    caption?: string;
    className?: string;
  };
}

const useStyles = makeStyles({
  content: {
    padding: '40px 32px 32px',
  },
});

const useTitleStyles = makeStyles({
  container: {
    padding: '20px 40px 0 32px',
  },
  closeButton: {
    position: 'absolute',
    top: 5,
    right: 8,
  },
  caption: {
    wordBreak: 'break-word',
  },
});

export const DialogTitle = ({
  children,
  onClose,
  caption,
  className,
  ...other
}: DialogTitleProps) => {
  const classes = useTitleStyles();

  return (
    <MuiDialogTitle
      disableTypography
      className={cn(classes.container, className)}
      {...other}>
      <div>
        <Typography variant='h4'>{children}</Typography>
        {!!caption && (
          <Text
            variant='normal'
            color={COLORS.COLOR_TEXT_LIGHTENED_20}
            className={classes.caption}>
            {caption}
          </Text>
        )}
      </div>
      {onClose ? (
        <IconButton
          className={classes.closeButton}
          aria-label='close'
          onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const Transition = forwardRef(function Transition(props: SlideProps, ref) {
  return <Slide direction='left' ref={ref} {...props} />;
});

function ModalV2({
  open,
  onClose,
  className,
  children,
  width,
  height,
  style,
  titleProps,
  classNameContent,
  ...props
}: ModalProps) {
  const classes = useStyles();

  return (
    <Dialog
      fullScreen={isMobile()}
      TransitionComponent={isMobile() ? Transition : undefined}
      className={className}
      open={open}
      onClose={onClose}
      aria-labelledby='modal-dialog-title'
      PaperProps={{
        elevation: 3,
        style: {
          width: isMobile() ? '100%' : width,
          height,
          maxWidth: width,
          ...style,
        },
      }}
      {...props}>
      {titleProps && (
        <DialogTitle
          id='customized-dialog-title'
          caption={titleProps.caption}
          onClose={onClose}
          className={titleProps.className}>
          {titleProps.title}
          {titleProps.titleIcon}
        </DialogTitle>
      )}
      <DialogContent className={cn(classes.content, classNameContent)}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export default ModalV2;
